/**
 * Turn a DRF validation body into one sentence a person can act on.
 *
 * A hub update failing looked, from the admin, like the Save button doing
 * nothing. The server had in fact answered precisely —
 *
 *     400 {"supervisor_ids":["The following supervisor IDs are invalid: [1761, 2164]"]}
 *
 * — and the page put that message beside the field it belonged to, which in a
 * modal that long is off-screen. The reason had to be read out of the server
 * log instead.
 */

/** Field names as they read in the form, not as the API spells them. */
const LABELS = {
  supervisor_ids: 'Supervisors',
  admin_ids: 'Admins',
  customer_engagement_admin_ids: 'Customer engagement admins',
  cashier_ids: 'Cashiers',
  place: 'Place',
  mobileno: 'Mobile number',
  delivery_fee: 'Delivery fee',
  delivery_mini_spent: 'Minimum spend',
  shop_open_at: 'Opens at',
  shop_close_at: 'Closes at',
  pincodes: 'Pincodes',
  hub_setup: 'Hub setup',
  promised_delivery_min_minutes: 'Delivery promise (from)',
  promised_delivery_max_minutes: 'Delivery promise (to)',
  franchise_id: 'Franchise',
  non_field_errors: '',
  detail: '',
  error: '',
};

const firstMessage = (value) => {
  if (Array.isArray(value)) {
    // Nested serializers answer with a list of per-row objects, and an
    // untouched row is `{}` — skipping those is what stops "Pincodes:
    // [object Object]".
    for (const entry of value) {
      const message = firstMessage(entry);
      if (message) return message;
    }
    return '';
  }
  if (value && typeof value === 'object') {
    for (const key of Object.keys(value)) {
      const message = firstMessage(value[key]);
      if (message) return `${LABELS[key] ?? key}: ${message}`;
    }
    return '';
  }
  return value == null ? '' : String(value);
};

/**
 * @param {object} body the response body of a 400
 * @param {number} [max] how many fields to name before summarising
 * @returns {string} e.g. "Supervisors: The following supervisor IDs are invalid: [1761, 2164]"
 */
export function describeServerErrors(body, max = 3) {
  if (!body || typeof body !== 'object') {
    return 'The server rejected the change but did not say why.';
  }

  const parts = [];
  Object.keys(body).forEach((key) => {
    const message = firstMessage(body[key]);
    if (!message) return;
    const label = LABELS[key] ?? key;
    parts.push(label ? `${label}: ${message}` : message);
  });

  if (!parts.length) {
    return 'The server rejected the change but did not say why.';
  }
  if (parts.length <= max) return parts.join(' · ');
  return `${parts.slice(0, max).join(' · ')} · and ${parts.length - max} more`;
}

export default describeServerErrors;
